import React from 'react';
import { Card, Tag, Statistic, Row, Col, Divider, Button, message } from 'antd';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    ApiOutlined,
    DatabaseOutlined,
    ClockCircleOutlined,
    DownloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

interface TaskDetailData {
    strategy: string;
    matrix_size: string;
    n: number;
    hit_rate: number;
    total_pairs: number;
    hit_count: number;
    miss_count: number;
    redis_total_pairs?: number;
    redis_hit_count?: number;
    redis_miss_count?: number;
    osrm_api_calls: number;
    cache_writes: number;
    duration_ms: number;
    missing_pairs?: number[][];
    row_stats?: any[];
    reorder_mapping?: any;
    partition_boundary?: number;
    partition_count?: number;
    partitions?: any[];
    query_blocks?: any[];
    timeline?: any;
}

interface TaskDetailViewProps {
    taskId: string;
    taskName: string;
    startedAt?: string;
    endedAt?: string;
    durationSeconds?: number;
    status: number;
    data: TaskDetailData;
}

const TaskDetailView: React.FC<TaskDetailViewProps> = ({
    taskId,
    taskName,
    startedAt,
    status,
    data
}) => {
    const formatDateTime = (dateTime?: string) => {
        if (!dateTime) return '-';
        return dayjs(dateTime).format('YYYY-MM-DD HH:mm:ss');
    };

    const getStrategyName = (strategy: string) => {
        switch (strategy) {
            case 'full_hit':
                return '策略1 - 完全命中';
            case 'direct_query':
                return '策略2 - 直接查询';
            case 'matrix_reorder_partition':
                return '策略3 - 矩阵重排分区';
            default:
                return strategy;
        }
    };

    const getStatusTag = (status: number) => {
        switch (status) {
            case 3:
                return <Tag color="success" icon={<CheckCircleOutlined />}>成功</Tag>;
            case 4:
                return <Tag color="error" icon={<CloseCircleOutlined />}>失败</Tag>;
            case 2:
                return <Tag color="processing" icon={<ClockCircleOutlined />}>运行中</Tag>;
            default:
                return <Tag color="default">初始化</Tag>;
        }
    };

    const hitRateColor = (rate: number) => {
        if (rate >= 0.75) return '#52c41a';
        if (rate >= 0.5) return '#faad14';
        return '#ff4d4f';
    };

    // 导出CSV
    const exportToCSV = () => {
        try {
            const csvRows = [
                ['任务ID', taskId],
                ['任务名称', taskName],
                ['开始时间', formatDateTime(startedAt)],
                ['执行时长(ms)', data.duration_ms],
                ['状态', status === 3 ? '成功' : status === 4 ? '失败' : status === 2 ? '运行中' : '初始化'],
                ['使用策略', getStrategyName(data.strategy)],
                ['矩阵大小', data.matrix_size],
                ['总点对数', data.total_pairs],
                ['命中率', `${(data.hit_rate * 100).toFixed(1)}%`],
                ['命中数量', data.hit_count],
                ['缺失数量', data.miss_count],
                ['API调用次数', data.osrm_api_calls],
                ['节省次数', Math.floor(data.total_pairs / 2) - data.osrm_api_calls],
                ['缓存写入', data.cache_writes]
            ];

            if (data.partition_boundary !== undefined) {
                csvRows.push(['分区边界', data.partition_boundary]);
            }
            if (data.partition_count !== undefined) {
                csvRows.push(['分区数量', data.partition_count]);
            }

            const csvContent = csvRows.map(row => row.join(',')).join('\n');
            const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `task_${taskId}_${dayjs().format('YYYYMMDDHHmmss')}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            message.success('CSV导出成功');
        } catch (error) {
            message.error('CSV导出失败');
            console.error('Export CSV error:', error);
        }
    };

    // 导出JSON
    const exportToJSON = () => {
        try {
            const exportData = {
                taskId,
                taskName,
                startedAt: formatDateTime(startedAt),
                status: status === 3 ? '成功' : status === 4 ? '失败' : status === 2 ? '运行中' : '初始化',
                ...data
            };

            const jsonContent = JSON.stringify(exportData, null, 2);
            const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `task_${taskId}_${dayjs().format('YYYYMMDDHHmmss')}.json`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            message.success('JSON导出成功');
        } catch (error) {
            message.error('JSON导出失败');
            console.error('Export JSON error:', error);
        }
    };

    return (
        <div className="space-y-6">
            {/* 标题栏 */}
            <Card>
                <div className="flex justify-between items-center">
                    <div>
                        <h2 className="text-2xl font-bold mb-2">
                            📊 任务详情 #{taskId}
                        </h2>
                        <div className="flex gap-4 text-gray-600">
                            <span>⏰ {formatDateTime(startedAt)}</span>
                            <span>⏱️ 耗时: {data.duration_ms}ms</span>
                            {getStatusTag(status)}
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={exportToCSV}
                        >
                            导出CSV
                        </Button>
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={exportToJSON}
                        >
                            导出JSON
                        </Button>
                    </div>
                </div>
            </Card>

            {/* 基础指标 */}
            <Card title="🎯 基础指标">
                {/* Redis维度指标 */}
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="text-center">
                            <Statistic
                                title="矩阵大小"
                                value={data.matrix_size}
                                valueStyle={{ fontSize: '24px' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="text-center">
                            <Statistic
                                title="Redis点对数"
                                value={data.redis_total_pairs ?? Math.floor(data.total_pairs / 2)}
                                valueStyle={{ fontSize: '24px' }}
                                suffix="对"
                            />
                            <div className="text-xs text-gray-500 mt-1">
                                对称去重后
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="text-center">
                            <Statistic
                                title="Redis命中"
                                value={data.redis_hit_count ?? Math.floor(data.hit_count / 2)}
                                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                                suffix="对"
                                valueStyle={{ fontSize: '20px', color: '#52c41a' }}
                            />
                            <div className="text-xs text-gray-500 mt-1">
                                {data.redis_total_pairs ? 
                                    `${((data.redis_hit_count ?? 0) / data.redis_total_pairs * 100).toFixed(1)}%` :
                                    `${((data.hit_rate * 100).toFixed(1))}%`
                                }
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="text-center">
                            <Statistic
                                title="Redis缺失"
                                value={data.redis_miss_count ?? Math.floor(data.miss_count / 2)}
                                prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                                suffix="对"
                                valueStyle={{ fontSize: '20px', color: '#ff4d4f' }}
                            />
                            <div className="text-xs text-gray-500 mt-1">
                                {data.redis_total_pairs ? 
                                    `${((data.redis_miss_count ?? 0) / data.redis_total_pairs * 100).toFixed(1)}%` :
                                    `${(((1 - data.hit_rate) * 100).toFixed(1))}%`
                                }
                            </div>
                        </Card>
                    </Col>
                </Row>

                <Divider />

                {/* 其他指标 */}
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="text-center">
                            <Statistic
                                title="Redis Key数"
                                value={data.cache_writes}
                                prefix={<DatabaseOutlined />}
                                suffix="个"
                                valueStyle={{ fontSize: '20px' }}
                            />
                            <div className="text-xs text-gray-500 mt-1">
                                最终写入Redis
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="text-center">
                            <Statistic
                                title="API调用"
                                value={data.osrm_api_calls}
                                prefix={<ApiOutlined />}
                                suffix="次"
                                valueStyle={{ fontSize: '20px' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="text-center">
                            <Statistic
                                title="使用策略"
                                value={getStrategyName(data.strategy)}
                                valueStyle={{ fontSize: '16px' }}
                            />
                        </Card>
                    </Col>
                </Row>
            </Card>
        </div>
    );
};

export default TaskDetailView;
